import React from 'react';
import { graphql, PageProps } from 'gatsby';
import { FormattedMessage } from 'react-intl';
import { Heading } from '@printdeal/ui-library';

import SEO from '../../components/Seo';
import Layout from '../../components/Layout';
import LayoutSection from '../../components/layout-section/LayoutSection';
import Breadcrumb from '../../components/Breadcrumb';
import BlogPostsGrid from '../../components/BlogPostsGrid';

import useSystemPages from '../../hooks/useSystemPages';
import { SystemPageSeoPropType } from '../../types/systemPageSeo';
import { IPageContext } from '../../types/gatsby';

interface NodeProps {
  id: string;
  title?: string;
  path?: string;
  featuredImage?: {
    url: string;
    title: string;
    width: number;
    height: number;
  };
  featuredDescription?: {
    featuredDescription: string;
  };
}

interface BlogTagPageProps {
  pageContent: SystemPageSeoPropType;
  allContentfulBlogPost: {
    nodes: NodeProps[];
  };
}

export const query = graphql`
  query($locale: String!, $contentful_id: String!) {
    pageContent: contentfulTag(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      title
      id
      seoTitle
      seoDescription {
        seoDescription
      }
      seoRobots {
        seoRobots
      }
      openGraphImage {
        ...OpenGraphImage
      }
    }
    allContentfulBlogPost(
      sort: { publishedDate: DESC }
      filter: {
        tags: { elemMatch: { contentful_id: { eq: $contentful_id } } }
        node_locale: { eq: $locale }
        slug: { ne: null }
        category: { slug: { ne: null } }
      }
    ) {
      nodes {
        id
        title
        path
        featuredImage {
          ...ContentfulCardImage
        }
      }
    }
  }
`;

const BlogTagPage = ({ data, pageContext }: PageProps<BlogTagPageProps, IPageContext>) => {
  const [blogIndex, blogTagsIndex] = useSystemPages('blogIndex', 'blogTagsIndex');
  const tag = data.pageContent;
  const posts = data.allContentfulBlogPost.nodes;

  return (
    <Layout localizedPaths={pageContext.localizedPaths}>
      <LayoutSection withSidebar={false} withDivider={false}>
        <Breadcrumb>
          <Breadcrumb.Item to={blogIndex.path}>
            <FormattedMessage id="blog.breadcrumb.blog.home" defaultMessage="Blog" />
          </Breadcrumb.Item>
          <Breadcrumb.Item to={blogTagsIndex.path}>
            <FormattedMessage id="blog.breadcrumb.blog.tags" defaultMessage="Tags" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>{tag.title}</Breadcrumb.Item>
        </Breadcrumb>

        <Heading as="h1" className="pd-mt-8 pd-text-center">
          {tag.title}
        </Heading>

        <section className="pd-mt-8">
          <BlogPostsGrid posts={posts} />
        </section>
      </LayoutSection>
    </Layout>
  );
};

export const Head = ({ data, pageContext }: PageProps<BlogTagPageProps, IPageContext>) => {
  const content = data.pageContent;
  return (
    <SEO
      locale={pageContext.locale}
      title={content.seoTitle}
      description={content.seoDescription?.seoDescription}
      robots={content.seoRobots?.seoRobots}
      alternates={pageContext.alternates}
      image={content.openGraphImage?.url}
    />
  );
};

export default BlogTagPage;
